/**
 * TodoSourceRow — the CD-25 container's "where does this live" band.
 *
 * Design handoff §1 (SOURCE row): a to-do **always** belongs to a client, and
 * belongs to a project / deal / ticket **only if it was created from one**.
 * Both chips navigate; the record chip unlinks with an ×; an unlinked to-do
 * shows a single dashed "＋ Link a project, deal or ticket".
 *
 * Why this band is load-bearing and not decoration: the record link IS the
 * to-do's project linkage (`source_type='project'` + `source_id`), and the
 * project is where a scheduling to-do reads its booking floor from — the
 * Delivery Call's deliver-by date. Before CD-25 there was no way to link a
 * hand-made to-do to a project, so a manual "schedule the delivery call" could
 * never pick up the rule. This row is that missing affordance.
 *
 * One deliberate deviation from the handoff: the design marks the client
 * "not editable here". The shipped modal has had an editable client since
 * Phase 5.2 (the client file's "+ Client To-Do" depends on it), so the client
 * chip is a PICKER wearing the design's chip — same grammar, no capability
 * lost. The record picker lists that client's projects, which is the link the
 * booking rules actually consume; existing deal / ticket / EOS links still
 * render and unlink, they just aren't re-pickable from here.
 *
 * The to-dos upgrade (2026-10-04): a caller that passes `onLinkRecord` gets the
 * design's full "＋ Link a project, deal or ticket" — one read of the client's
 * projects, deals and tickets (GET /api/todos/link-targets), grouped. Callers
 * that only pass `onLinkProject` keep the projects-only picker unchanged.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './todoContainer.module.css';
import TodoMenu, { type TodoMenuOption } from './TodoMenu';
import { projectsApi } from '../../services/api';
import { projectStageLabel } from '../projects/projectsBoardConfig';
import { todosApi, type TodoLinkTargets } from '../../services/todosApi';

/** A record the SOURCE band can link a to-do to. */
export interface TodoLinkPick { kind: 'project' | 'deal' | 'ticket'; id: number; label: string }

export interface TodoSourceClient { card_id: string; title: string }

export interface TodoSourceRecord {
  /** Uppercase qualifier shown on the chip. */
  kind: 'PROJECT' | 'DEAL' | 'TICKET' | 'EOS' | 'CARD';
  label: string;
  /** Fired on chip click — opens that record. Omit to render it non-navigating. */
  onOpen?: () => void;
}

export interface TodoSourceRowProps {
  clients: TodoSourceClient[];
  clientCardId: string | null;
  clientName: string | null;
  onPickClient: (clientCardId: string | null, clientTitle: string | null) => void;
  onOpenClient?: () => void;
  /** The linked record, or null when the to-do is standalone. */
  record: TodoSourceRecord | null;
  /** Link the to-do to a project (writes source_type='project' + source_id). */
  onLinkProject: (projectId: number, title: string) => void;
  /** Drop the record link (back to standalone). **Omit it** for record kinds
   *  the picker cannot re-create — unlinking a deal / ticket / EOS link here
   *  would be one un-confirmed click away from destroying a link that has no
   *  route back, and (for eos_rock_milestone) silently breaking the
   *  todo-completes-the-milestone sync in todoService.completeTodo. */
  onUnlinkRecord?: () => void;
  /** Render the "＋ Link a project" affordance ALONGSIDE an existing non-project
   *  record chip. Without it, a deal- or ticket-attached scheduling to-do is
   *  refused at mint time ("link this to a project first") with no way to
   *  actually do so — the record chip isn't a project and its unlink is
   *  deliberately withheld. */
  alsoOfferProjectLink?: boolean;
  /** Read-only mode (a completed to-do still shows its provenance). */
  disabled?: boolean;
  /** The design's full picker: projects, deals AND tickets on the client. When
   *  given it replaces the projects-only list (and `onLinkProject` is unused). */
  onLinkRecord?: (pick: TodoLinkPick) => void;
  /** Right-aligned at the end of the band ("Assigned by Ben · Oct 2"). */
  trailing?: React.ReactNode;
}

const TodoSourceRow: React.FC<TodoSourceRowProps> = ({
  clients,
  clientCardId,
  clientName,
  onPickClient,
  onOpenClient,
  record,
  onLinkProject,
  onUnlinkRecord,
  alsoOfferProjectLink = false,
  disabled = false,
  onLinkRecord,
  trailing,
}) => {
  const [clientOpen, setClientOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [projects, setProjects] = useState<Array<{ id: number; title: string; status?: string }>>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [targets, setTargets] = useState<TodoLinkTargets | null>(null);
  // The menus are portaled out of the paper (it clips), so each one needs its
  // chip as an explicit anchor to position against.
  const clientAnchor = useRef<HTMLButtonElement | null>(null);
  const projectAnchor = useRef<HTMLButtonElement | null>(null);

  // Load the client's projects only when the picker is first opened — the
  // common path (a to-do that is already linked, or has no client) makes no
  // request at all.
  useEffect(() => {
    if (!recordOpen || !clientCardId) return;
    let alive = true;
    setProjectsLoading(true);
    if (onLinkRecord) {
      todosApi.linkTargets(clientCardId)
        .then((r) => { if (alive) setTargets(r.data ?? null); })
        .catch(() => { if (alive) setTargets(null); })
        .finally(() => { if (alive) setProjectsLoading(false); });
      return () => { alive = false; };
    }
    projectsApi.getProjects({ client_card_id: clientCardId })
      .then((r: any) => { if (alive) setProjects(Array.isArray(r?.data) ? r.data : []); })
      .catch(() => { if (alive) setProjects([]); })
      .finally(() => { if (alive) setProjectsLoading(false); });
    return () => { alive = false; };
  }, [recordOpen, clientCardId, onLinkRecord]);

  const clientOptions: TodoMenuOption[] = [
    { value: null, label: 'No client' },
    ...clients.map((c) => ({ value: c.card_id, label: c.title })),
  ];

  const projectOptions: TodoMenuOption[] = projectsLoading
    ? [{ value: '__loading', label: onLinkRecord ? 'Loading…' : 'Loading projects…', disabled: true }]
    // meta = the project's stage in the model's words — never the raw status key.
    : onLinkRecord
      ? [
          ...(targets?.projects ?? []).filter((p) => !p.done).map((p) => ({ value: `project:${p.id}`, label: p.title, meta: p.status ? projectStageLabel(p.status) : null, group: 'Projects' })),
          ...(targets?.deals ?? []).map((d) => ({ value: `deal:${d.id}`, label: d.name, meta: d.stage || d.status || null, group: 'Deals' })),
          ...(targets?.tickets ?? []).map((t) => ({ value: `ticket:${t.id}`, label: t.title, meta: t.status || null, group: 'Tickets' })),
        ]
      : projects.map((p) => ({ value: p.id, label: p.title, meta: p.status ? projectStageLabel(p.status) : null }));

  const handlePickClient = useCallback((value: string | number | null) => {
    const next = value == null ? null : String(value);
    const matched = clients.find((c) => c.card_id === next);
    onPickClient(next, matched?.title ?? null);
  }, [clients, onPickClient]);

  return (
    <div className={styles.sourceRow}>
      <span className={styles.kicker} style={{ marginRight: 2 }}>Source</span>

      {/* CLIENT — always present. A to-do without one says so honestly. */}
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <button
          ref={clientAnchor}
          type="button"
          className={clientCardId ? styles.chip : styles.chipDashed}
          disabled={disabled}
          onClick={() => setClientOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={clientOpen}
          title={clientCardId ? 'Change the client' : 'Attach a client'}
        >
          {clientCardId && <span className={styles.diamondIndigo} />}
          <span className={clientCardId ? styles.chipName : undefined}>
            {clientCardId ? (clientName || 'Client') : '＋ Attach a client'}
          </span>
          {clientCardId && <span className={styles.chipQualifier}>Client</span>}
          <span className={styles.chipCaret}>▾</span>
        </button>
        <TodoMenu
          open={clientOpen}
          onClose={() => setClientOpen(false)}
          anchorRef={clientAnchor}
          header="Attach a client"
          options={clientOptions}
          value={clientCardId}
          onPick={handlePickClient}
          width="wide"
          filterPlaceholder="Search clients…"
          emptyLabel="No active clients"
        />
      </span>

      {/* The client chip opens the client record; kept as a separate affordance
          so the chip itself can stay the picker. */}
      {clientCardId && onOpenClient && (
        <button
          type="button"
          className={styles.unlinkBtn}
          onClick={onOpenClient}
          title="Open this client"
          aria-label="Open this client"
        >
          ↗
        </button>
      )}

      <span className={styles.sep}>›</span>

      {record ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          <button
            type="button"
            className={styles.chip}
            onClick={record.onOpen}
            disabled={!record.onOpen}
            title={record.onOpen ? `Open this ${record.kind.toLowerCase()}` : record.label}
          >
            <span className={styles.chipName}>{record.label}</span>
            <span className={styles.chipQualifier}>{record.kind}</span>
          </button>
          {!disabled && onUnlinkRecord && (
            <button
              type="button"
              className={styles.unlinkBtn}
              onClick={onUnlinkRecord}
              title="Unlink from this record"
              aria-label="Unlink from this record"
            >
              ×
            </button>
          )}
        </span>
      ) : null}

      {(!record || alsoOfferProjectLink) && (
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <button
            ref={projectAnchor}
            type="button"
            className={styles.chipDashed}
            disabled={disabled || !clientCardId}
            onClick={() => setRecordOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={recordOpen}
            title={clientCardId
              ? (onLinkRecord ? 'Link this to-do to a project, deal or ticket' : 'Link this to-do to a project')
              : (onLinkRecord ? 'Attach a client first — projects, deals and tickets are listed per client' : 'Attach a client first — projects are listed per client')}
          >
            {onLinkRecord ? '＋ Link a project, deal or ticket' : '＋ Link a project'}
          </button>
          <TodoMenu
            open={recordOpen}
            onClose={() => setRecordOpen(false)}
            anchorRef={projectAnchor}
            header={onLinkRecord ? 'Link a project, deal or ticket' : 'Link a project'}
            options={projectOptions}
            value={null}
            onPick={(value) => {
              if (onLinkRecord) {
                const m = /^(project|deal|ticket):(\d+)$/.exec(String(value ?? ''));
                if (!m) return;
                const kind = m[1] as TodoLinkPick['kind'];
                const id = Number(m[2]);
                const label = kind === 'project'
                  ? targets?.projects.find((p) => p.id === id)?.title
                  : kind === 'deal'
                    ? targets?.deals.find((d) => d.id === id)?.name
                    : targets?.tickets.find((t) => t.id === id)?.title;
                onLinkRecord({ kind, id, label: label ?? `${kind} #${id}` });
                return;
              }
              const id = Number(value);
              if (!Number.isFinite(id)) return;
              const p = projects.find((x) => x.id === id);
              if (p) onLinkProject(p.id, p.title);
            }}
            width="wide"
            filterPlaceholder={onLinkRecord ? 'Search projects, deals, tickets…' : 'Search projects…'}
            emptyLabel={onLinkRecord ? 'Nothing on this client to link yet' : 'This client has no projects yet'}
          />
        </span>
      )}
      {trailing && <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-faint)' }}>{trailing}</span>}
    </div>
  );
};

export default TodoSourceRow;
